import { BasemapSettings } from 'features/account/types'
import { Branding } from 'features/branding/models/Branding'
import { Agent } from './agent-types'
import { CommonPrivacy, CommonSharingScope, RequestStatus, SharedUser, SortDirection } from 'features/common/types'
import { ProviderType } from 'features/connections/types'
import { Group } from 'features/groups'
import { QueryParametersSimple } from 'features/sql/types'
import { Comment, CommentResponse } from 'features/collaboration/types'
import { CartoMapConfigEnvelope } from './map-config/map-config'

export { SqlParameterType } from './map-config/sql-parameter-types'
export type {
  SqlParameter,
  SqlParameterItem,
  SqlCategoryParameter,
  SqlDateRangeParameter,
  SqlNumericParameter,
  SqlNumericRangeParameter
} from './map-config/sql-parameter-types'

export { WidgetType } from './map-config/widget-types'
export type { CartoMapLayer, CartoMapLayerConfig, CartoMapLayerVisConfig } from './map-config/layer-types'
export type { CustomBaseMapSettings, BasemapConfig } from './map-config/basemap-types'
export type {
  BuilderUiPersistenceState,
  CartoMapConfigEnvelope,
  CartoKeplerMapConfigEnvelope,
  CartoKeplerMapConfig,
  CartoMapConfig,
  MapSettings
} from './map-config/map-config'

export enum Formats {
  GEOJSON = 'geojson',
  TILEJSON = 'tilejson',
  JSON = 'json',
  RASTER = 'raster'
}

export enum MapType {
  table = 'table',
  query = 'query',
  tileset = 'tileset',
  raster = 'raster'
}

export enum GeomType {
  Points = 'points',
  Lines = 'lines',
  Polygons = 'polygons',
  Unknown = 'unknown'
}

export type MapPrivacy = CommonPrivacy
export const MapPrivacy = { ...CommonPrivacy }

export enum BasemapFormat {
  WMTS = 'wmts',
  TileJson = 'tilejson',
  Raster = 'raster'
}

export interface BasemapStyleSettings {
  url: string
  tileSize?: number
}

export interface Basemap {
  id: string
  name: string
  attribution?: string
  settings: BasemapStyleSettings
  type: BasemapFormat
  thumbnail: string

  // internal
  disabled?: boolean
}

export type MapLockStatus = {
  locked: boolean
  userId?: string
  email?: string
  pictureUrl?: string
}

export type CartoMap = {
  id: string
  title?: string
  description?: string
  thumbnailUrl?: string
  ownerPicture?: string
  ownerEmail?: string
  ownerIsCartoUser?: boolean
  views?: number
  tags?: string[]

  keplerMapConfig?: CartoMapConfigEnvelope
  createdAt?: string
  updatedAt?: string
  accountId?: string
  ownerId?: string
  token?: string
  privacy?: MapPrivacy
  lastPublishedAt?: string
  publishedWithPassword?: boolean
  publicMapPassword?: string
  groups?: Group[]
  users?: SharedUser[]
  collaborative?: boolean
  lockStatus?: MapLockStatus
  sharingScope?: CommonSharingScope
  agentEnabled?: boolean
  // only in public map, as we don't have access to these data using mapToken
  branding?: Branding
  basemaps?: {
    settings: BasemapSettings
    customBasemaps: Basemap[]
  }

  agent?: Agent
}
// old name used throughout codebase, but it's confusing with ECMAScript Map, so better to use CartoMap
export type Map = CartoMap

export interface DemoMap extends Map {
  templateUrl: string
  exists: boolean
  businessVerticals: string[]
}

export interface MapDataset {
  connectionViewerCredentials?: boolean
  id: string
  type: MapType
  source: string
  format?: Formats
  geoColumn?: string | null
  aggregationExp?: string | null
  aggregationResLevel?: number | null
  connectionId?: string
  connectionName?: string
  providerId: ProviderType
  createdAt?: string
  updatedAt?: string
  label?: string
  color?: string
  uniqueIdProperty?: string
  queryParameters?: QueryParametersSimple | null
  queryTemplate?: string | null
  name?: string | null
  spatialIndex?: string | null
  columns?: string[] | null

  /**
   * Indicates if export to bucket is available for connection this dataset belongs to.
   *
   * Only available in datasets returned from public map endpoint.
   */
  exportToBucketAvailable?: boolean

  //
  // extra fields in builder, in-memory only
  //

  /**
   * `accessToken` to be used to fetch this map dataset data.
   *
   * In certain scenarios, we need to use `map.token` to fetch this map dataset data e.g
   *   - public maps,
   *   - viewer mode without viewer credentials or CartoDW connection
   *
   * See `assignTokensToDatasets`.
   */
  accessToken?: string

  runningSince?: number
  runningTo?: number
  jobId?: string
  status?: RequestStatus
  errorMessage?: string

  /**
   * In mem only.
   *
   * Initialized during map load, with date derived from `mapSettings.cacheTime` and resetted
   * on datasetrefresh.
   *
   * Determines `max-age` of all requests connected with this dataset.
   */
  lastRefreshAt?: number // milliseconds

  sourceWorkflowId?: string | null
  sourceWorkflowNodeId?: string | null
}

export interface MapsQueryFilters {
  search?: string
  filter?: MapsFilter
  order?: MapsOrder
  aiEnabled?: string
  pagination?: {
    size?: number
    page: number
  }
  refresh?: boolean
}

export interface MapListFilters {
  search: string
  pageSize?: number
  order: MapsOrder
  permissions?: MapOwnerFilter
  tags?: string[]
  aiEnabled?: string
}

export interface MapsFilter {
  permissions?: MapOwnerFilter
  tags?: string[]
}

export interface RawMapListFilters extends Omit<MapListFilters, 'permissions'> {
  permissions?: {
    user_id?: string
    privacy?: MapPrivacy
  }
}

export interface RawMapsQueryFilters extends Omit<MapsQueryFilters, 'filter'> {
  filter: RawMapsFilter
}

export interface RawMapsFilter {
  owner?: string
  privacy?: MapPrivacy
  tags?: string[]
  aiEnabled?: string
}

export interface MapsOrder {
  by: MapFieldToOrderBy
  direction: SortDirection
}

export enum MapFieldToOrderBy {
  Title = 'title',
  UpdatedAt = 'updated_at'
}

export interface MapEmptyCardProps {
  onClick: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  img: string | any
}

export interface MapDetailsRequest {
  id: string
  privacy: MapPrivacy
  headers?: Record<string, string>
}

export interface SetMapPrivacyRequest {
  mapId: string
  privacy: MapPrivacy
  publicPassword?: string | null
  groupIds?: string[]
  userIds?: string[]
  sharingScope?: CommonSharingScope
}

export interface DuplicateMapProps {
  mapId: string
}

export enum DuplicateMapType {
  Full = 'fully',
  Partial = 'partially'
}

export interface DuplicateMapResponse {
  status: DuplicateMapType
}
export interface DryRunDuplicateMapResponse {
  status: DuplicateMapType
  restricted_connections?: string[]
}

export interface CreateDemoMapRequest {
  id: string
  url: string
}

export enum MapActions {
  CreateFromTemplate = 'createFromTemplate',
  Duplicate = 'duplicate',
  Delete = 'delete',
  Edit = 'edit',
  Share = 'share',
  RemoveCredentials = 'removeCredentials'
}

export interface MapConfig {
  zoom?: number
  lat?: number
  lng?: number
  pitch?: number
  bearing?: number
  layers?: number[]
  /** Map of `widgetId` (not `sourceId`) to `filterValues`. */
  filters?: Record<string, string[]>
  spatialFilter?: { geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon }
  search?: string
  sqlParameters?: Record<string, string[]>
}

export enum MapOwnerFilter {
  All = 'all',
  Mine = 'mine',
  Shared = 'shared',
  Public = 'public'
}

export interface MapCommentAnchor {
  latitude: number
  longitude: number
  zoom?: number // we need to store this because comment should lead to same view on map
  mapIndex: number
}

export interface MapComment extends Comment {
  mapId: string
  anchor: MapCommentAnchor
}

export interface MapCommentResponse extends CommentResponse {
  mapId: string
}

export interface MapCommentWithResponses extends MapComment {
  responses: MapCommentResponse[]
}

export const isMapCommentResponse = (comment: MapComment | MapCommentResponse): comment is MapCommentResponse => {
  return 'commentId' in comment
}
